import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageSizeSelectV8, { readPageSizeV8 } from './PageSizeSelectV8';
import {
  Button,
  Form,
  Label,
  Popup,
  Pagination,
  Table,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import {
  API,
  copy,
  showError,
  showInfo,
  showSuccess,
  showWarning,
  timestamp2string,
} from '../helpers';

import { renderQuota } from '../helpers/render';

function renderTimestamp(timestamp) {
  return <>{timestamp2string(timestamp)}</>;
}

function renderStatus(status, t) {
  switch (status) {
    case 1:
      return (
        <Label basic color='green'>
          {t('redemption.status.unused')}
        </Label>
      );
    case 2:
      return (
        <Label basic color='red'>
          {t('redemption.status.disabled')}
        </Label>
      );
    case 3:
      return (
        <Label basic color='grey'>
          {t('redemption.status.used')}
        </Label>
      );
    default:
      return (
        <Label basic color='black'>
          {t('redemption.status.unknown')}
        </Label>
      );
  }
}

const RedemptionsTable = () => {
  const { t } = useTranslation();
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [pageSize, setPageSize] = useState(() =>
    readPageSizeV8('v8-page-size-redemptions', 10)
  );

  const loadRedemptions = async (startIdx) => {
    const res = await API.get(
      `/api/redemption/?p=${startIdx}&page_size=${pageSize}`
    );
    const { success, message, data } = res.data;
    if (success) {
      if (startIdx === 0) {
        setRedemptions(data);
      } else {
        const newRedemptions = [...redemptions];
        newRedemptions.splice(startIdx * pageSize, data.length, ...data);
        setRedemptions(newRedemptions);
      }
    } else {
      showError(message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(redemptions.length / pageSize) + 1) {
        // In this case we have to load more data and then append them.
        await loadRedemptions(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  useEffect(() => {
    loadRedemptions(0)
      .then()
      .catch((reason) => {
        showError(reason);
      });
  }, [pageSize]);

  const manageRedemption = async (id, action, idx) => {
    let data = { id };
    let res;
    switch (action) {
      case 'delete':
        res = await API.delete(`/api/redemption/${id}/`);
        break;
      case 'enable':
        data.status = 1;
        res = await API.put('/api/redemption/?status_only=true', data);
        break;
      case 'disable':
        data.status = 2;
        res = await API.put('/api/redemption/?status_only=true', data);
        break;
    }
    const { success, message } = res.data;
    if (success) {
      showSuccess(t('token.messages.operation_success'));
      let redemption = res.data.data;
      let newRedemptions = [...redemptions];
      let realIdx = (activePage - 1) * pageSize + idx;
      if (action === 'delete') {
        newRedemptions[realIdx].deleted = true;
      } else {
        newRedemptions[realIdx].status = redemption.status;
      }
      setRedemptions(newRedemptions);
    } else {
      showError(message);
    }
  };

  const searchRedemptions = async () => {
    if (searchKeyword === '') {
      // if keyword is blank, load files instead.
      await loadRedemptions(0);
      setActivePage(1);
      return;
    }
    setSearching(true);
    const res = await API.get(
      `/api/redemption/search?keyword=${searchKeyword}`
    );
    const { success, message, data } = res.data;
    if (success) {
      setRedemptions(data);
      setActivePage(1);
    } else {
      showError(message);
    }
    setSearching(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortRedemption = (key) => {
    if (redemptions.length === 0) return;
    setLoading(true);
    let sortedRedemptions = [...redemptions];
    sortedRedemptions.sort((a, b) => {
      if (!isNaN(a[key])) {
        // If the value is numeric, subtract to sort
        return a[key] - b[key];
      } else {
        // If the value is not numeric, sort as strings
        return ('' + a[key]).localeCompare(b[key]);
      }
    });
    if (sortedRedemptions[0].id === redemptions[0].id) {
      sortedRedemptions.reverse();
    }
    setRedemptions(sortedRedemptions);
    setLoading(false);
  };

  const refresh = async () => {
    setLoading(true);
    await loadRedemptions(0);
    setActivePage(1);
  };

  const handlePageSizeChange = (nextPageSize) => {
    setLoading(true);
    setActivePage(1);
    setPageSize(nextPageSize);
  };

  return (
    <>
      <Form onSubmit={searchRedemptions}>
        <Form.Input
          icon='search'
          fluid
          iconPosition='left'
          placeholder={t('redemption.search')}
          value={searchKeyword}
          loading={searching}
          onChange={handleKeywordChange}
        />
      </Form>

      <div
        className='management-table-scroll-v72'
        role='region'
        aria-label='Scrollable data table'
        tabIndex={0}
      >
        <Table
          basic={'very'}
          compact
          size='small'
          unstackable
          className='management-table-v72 management-redemption-table-v72'
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('id');
                }}
              >
                {t('redemption.table.id')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('name');
                }}
              >
                {t('redemption.table.name')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('status');
                }}
              >
                {t('redemption.table.status')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('quota');
                }}
              >
                {t('redemption.table.quota')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('created_time');
                }}
              >
                {t('redemption.table.created_time')}
              </Table.HeaderCell>
              <Table.HeaderCell
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  sortRedemption('redeemed_time');
                }}
              >
                {t('redemption.table.redeemed_time')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('redemption.table.actions')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {redemptions
              .slice((activePage - 1) * pageSize, activePage * pageSize)
              .map((redemption, idx) => {
                if (redemption.deleted) return <></>;
                return (
                  <Table.Row key={redemption.id}>
                    <Table.Cell>{redemption.id}</Table.Cell>
                    <Table.Cell>
                      {redemption.name
                        ? redemption.name
                        : t('redemption.table.no_name')}
                    </Table.Cell>
                    <Table.Cell>
                      {renderStatus(redemption.status, t)}
                    </Table.Cell>
                    <Table.Cell>{renderQuota(redemption.quota, t)}</Table.Cell>
                    <Table.Cell>
                      {renderTimestamp(redemption.created_time)}
                    </Table.Cell>
                    <Table.Cell>
                      {redemption.redeemed_time
                        ? renderTimestamp(redemption.redeemed_time)
                        : t('redemption.table.not_redeemed')}{' '}
                    </Table.Cell>
                    <Table.Cell>
                      <div>
                        <Button
                          size={'tiny'}
                          positive
                          onClick={async () => {
                            if (await copy(redemption.key)) {
                              showSuccess(t('token.messages.copy_success'));
                            } else {
                              showWarning(t('token.messages.copy_failed'));
                              setSearchKeyword(redemption.key);
                            }
                          }}
                        >
                          {t('redemption.buttons.copy')}
                        </Button>
                        <Popup
                          trigger={
                            <Button size='tiny' negative>
                              {t('redemption.buttons.delete')}
                            </Button>
                          }
                          on='click'
                          flowing
                          hoverable
                        >
                          <Button
                            negative
                            onClick={() => {
                              manageRedemption(redemption.id, 'delete', idx);
                            }}
                          >
                            {t('redemption.buttons.confirm_delete')}
                          </Button>
                        </Popup>
                        <Button
                          size={'tiny'}
                          disabled={redemption.status === 3} // used
                          onClick={() => {
                            manageRedemption(
                              redemption.id,
                              redemption.status === 1 ? 'disable' : 'enable',
                              idx
                            );
                          }}
                        >
                          {redemption.status === 1
                            ? t('redemption.buttons.disable')
                            : t('redemption.buttons.enable')}
                        </Button>
                        <Button
                          size={'tiny'}
                          as={Link}
                          to={'/redemption/edit/' + redemption.id}
                        >
                          {t('redemption.buttons.edit')}
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>

          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan='7'>
                <Button
                  size='small'
                  as={Link}
                  to='/redemption/add'
                  loading={loading}
                >
                  {t('redemption.buttons.add')}
                </Button>
                <Button size='small' onClick={refresh} loading={loading}>
                  {t('redemption.buttons.refresh')}
                </Button>
                <PageSizeSelectV8
                  label={t('log.table.page_size')}
                  storageKey='v8-page-size-redemptions'
                  value={pageSize}
                  onChange={handlePageSizeChange}
                />
                <Pagination
                  floated='right'
                  activePage={activePage}
                  onPageChange={onPaginationChange}
                  size='small'
                  siblingRange={1}
                  totalPages={
                    Math.ceil(redemptions.length / pageSize) +
                    (redemptions.length % pageSize === 0 ? 1 : 0)
                  }
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>
    </>
  );
};

export default RedemptionsTable;
